import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api.service';
import { useTenantStore } from '../../stores/tenant.store';
import { usePermissions } from '../../context/PermissionsContext';

type LegalEntityOption = {
  id: string;
  code: string;
  legalName: string;
  branches?: { id: string; code: string; name: string; isActive?: boolean }[];
};

type BranchOption = { id: string; code: string; name: string };

type TenantSlice = {
  legalEntityId: string | null;
  branchId: string | null;
  setLegalEntityId: (id: string | null) => void;
  setBranchId: (id: string | null) => void;
};

const selectClass =
  'h-8 max-w-[10.5rem] truncate rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400';

/**
 * Selectores Empresa → Sucursal (contexto multiempresa Fase 1).
 */
export function TenantContextBar() {
  const { session } = usePermissions();
  const legalEntityId = useTenantStore((s: TenantSlice) => s.legalEntityId);
  const branchId = useTenantStore((s: TenantSlice) => s.branchId);
  const setLegalEntityId = useTenantStore((s: TenantSlice) => s.setLegalEntityId);
  const setBranchId = useTenantStore((s: TenantSlice) => s.setBranchId);

  const [entities, setEntities] = useState<LegalEntityOption[]>([]);
  const [branches, setBranches] = useState<BranchOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await apiService.listLegalEntities();
        if (cancelled) return;
        setEntities(list ?? []);
      } catch {
        if (!cancelled) setEntities([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const visibleEntities = useMemo(() => {
    const allowed = session?.allowedLegalEntityIds;
    if (allowed === null || allowed === undefined) return entities;
    if (allowed.length === 0) return [];
    return entities.filter((e) => allowed.includes(e.id));
  }, [entities, session?.allowedLegalEntityIds]);

  useEffect(() => {
    if (visibleEntities.length === 1 && !legalEntityId) {
      setLegalEntityId(visibleEntities[0].id);
    }
  }, [visibleEntities, legalEntityId, setLegalEntityId]);

  useEffect(() => {
    if (!legalEntityId) {
      setBranches([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await apiService.getBranches(true, legalEntityId);
        if (cancelled) return;
        const allowed = session?.allowedBranchIds;
        const filtered = Array.isArray(allowed)
          ? (list ?? []).filter((b) => allowed.includes(b.id))
          : list ?? [];
        setBranches(filtered);
      } catch {
        if (!cancelled) setBranches([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [legalEntityId, session?.allowedBranchIds, session?.user?.branchId]);

  useEffect(() => {
    if (!branchId) return;
    if (branches.length && !branches.some((b) => b.id === branchId)) {
      setBranchId(null);
    }
  }, [branches, branchId, setBranchId]);

  if (!session) return null;
  if (visibleEntities.length === 0) return null;

  return (
    <div className="flex shrink-0 items-end gap-2.5">
      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          Empresa
        </span>
        <select
          className={selectClass}
          value={legalEntityId ?? ''}
          onChange={(e) => setLegalEntityId(e.target.value || null)}
          aria-label="Empresa"
        >
          {visibleEntities.length > 1 && <option value="">Seleccionar…</option>}
          {visibleEntities.map((e) => (
            <option key={e.id} value={e.id}>
              {e.legalName}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          Sucursal
        </span>
        <select
          className={`${selectClass} max-w-[9rem]`}
          value={branchId ?? ''}
          onChange={(e) => setBranchId(e.target.value || null)}
          disabled={!legalEntityId}
          aria-label="Sucursal"
        >
          <option value="">Todas</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
